import { ComponentType } from 'react';
import { useFormState } from 'react-final-form';
import Button from 'components/Button';
import {
    FieldsContainer, FormFooter, StyledModal, StyledUniversalForm, ModalTitle, GlobalStyle,
} from './styled';
import Fields from './fields';
import { ModelConfig } from '../types';
import isBuiltinModel from '../helpers/isBuiltinModel';

interface AiModelConfigFormProps {
    initialValues?: any;
    onSubmit?: (values: ModelConfig) => void;
    onRequestClose: () => void;
    fieldsComponent?: ComponentType;
    onTest?: (values: ModelConfig) => void;
}

const getModel = (modelId: string = '') => {
    if (isBuiltinModel(modelId)) {
        return 'openai';
    }
    return 'fine-tuned';
};

const getModelId = (modelId: string = '') => {
    if (isBuiltinModel(modelId) && modelId.startsWith('gpt-3.5')) return '';
    return modelId;
};

interface FormValues extends Record<string, unknown> {
    temperature: string | number;
    model: string;
    modelId?: string
}

const mapInitialValuesToFormValues = (initialValues: any): FormValues => {
    const { model, temperature, ...rest } = initialValues;
    return {
        temperature,
        model: getModel(model.id),
        modelId: getModelId(model.id),
        ...rest,
    };
};

const mapFormValuesToConfig = (currentValues: FormValues): ModelConfig => {
    const {
        temperature, model, modelId, ...rest
    } = currentValues;
    return {
        model: {
            id: modelId || model,
            name: modelId || model,
        },
        temperature: parseFloat(temperature as string),
        ...rest,
    };
};

const TestConfigurationButton = ({ onTest }: { onTest: (values: ModelConfig) => void }) => {
    const { values, invalid, submitting } = useFormState();
    return <Button type="button" label="Test configuration" variant="outline-brand" borderRadius="semi-square" disabled={invalid || submitting} onClick={() => onTest(mapFormValuesToConfig(values as FormValues))} />;
};

const AiModelConfigForm = ({
    initialValues = {},
    onSubmit = () => {},
    onRequestClose = () => {},
    fieldsComponent: FieldsComponent = Fields,
    onTest,
}: AiModelConfigFormProps) => (
    <StyledModal
        size="large"
        onRequestClose={onRequestClose}
        isOpen
    >
        <GlobalStyle />
        <StyledUniversalForm
            onSubmit={(values) => onSubmit(mapFormValuesToConfig(values as FormValues))}
            initialValues={mapInitialValuesToFormValues({
                temperature: 1,
                model: {
                    id: 'gpt-3.5-turbo-0125',
                    name: 'gpt-3.5-turbo-0125',
                },
                ...initialValues,
            })}
        >
            <ModalTitle>Configuration</ModalTitle>
            <FieldsContainer>
                <FieldsComponent />
            </FieldsContainer>
            <FormFooter>
                {onTest && <TestConfigurationButton onTest={onTest} />}
                <Button
                    label="Cancel"
                    variant="outline-brand"
                    borderRadius="semi-square"
                    onClick={onRequestClose}
                />
                <Button
                    type="submit"
                    label="Save"
                    variant="brand"
                    borderRadius="semi-square"
                />
            </FormFooter>
        </StyledUniversalForm>
    </StyledModal>
);

export default AiModelConfigForm;
